import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, Star, Stethoscope } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "../components/ui/sheet";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api } from "../lib/api";

const FALLBACK_DOCTOR_IMG =
  "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=400&q=80";

function DoctorCard({ d, onClick }) {
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKey}
      data-testid={`doctor-card-${d.id}`}
      className="text-left bg-white border border-border rounded-2xl p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 flex flex-col gap-4 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30"
    >
      <div className="flex items-center gap-4">
        <img
          src={d.image_url || FALLBACK_DOCTOR_IMG}
          alt={d.name}
          className="h-16 w-16 rounded-xl object-cover"
        />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-base truncate">{d.name}</div>
          <div className="text-xs text-muted-foreground truncate">{d.category}</div>
          <div className="text-xs text-muted-foreground truncate mt-0.5">{d.qualification}</div>
        </div>
      </div>
      <div className="flex items-end justify-between">
        <div className="flex flex-col gap-1 text-xs">
          <span className="inline-flex items-center gap-1 text-amber-600">
            <Star className="h-3.5 w-3.5 fill-current" />
            <span className="font-medium">{d.rating?.toFixed?.(1) ?? d.rating}</span>
            <span className="text-muted-foreground">({d.total_reviews ?? 0})</span>
          </span>
          <span className="text-muted-foreground">
            {d.experience_years} yrs exp
          </span>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">From</div>
          <div className="text-lg font-semibold tracking-tight">
            ₹{Math.min(d.video_fee || d.consultation_fee, d.opd_fee || d.consultation_fee)}
          </div>
        </div>
      </div>
      <Button
        data-testid={`book-doctor-${d.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground h-11"
      >
        Book appointment
      </Button>
    </div>
  );
}

export default function PatientDoctors() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const initialQ = params.get("q") || "";
  const initialCategory = params.get("category") || "all";

  const [q, setQ] = useState(initialQ);
  const [category, setCategory] = useState(initialCategory);
  const [sort, setSort] = useState("rating");
  const [feeRange, setFeeRange] = useState([0, 1500]);
  const [minRating, setMinRating] = useState(0);
  const [mode, setMode] = useState("any");

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || []));
  }, []);

  const fetchDoctors = useMemo(
    () => async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        if (q) qs.set("q", q);
        if (category && category !== "all") qs.set("category", category);
        if (sort) qs.set("sort", sort);
        if (feeRange[0] > 0) qs.set("min_fee", String(feeRange[0]));
        if (feeRange[1] < 1500) qs.set("max_fee", String(feeRange[1]));
        if (minRating > 0) qs.set("min_rating", String(minRating));
        if (mode && mode !== "any") qs.set("mode", mode);

        const { data } = await api.get(`/doctors?${qs.toString()}`);
        setItems(data.items || []);
        setTotal(data.total || 0);
      } finally {
        setLoading(false);
      }
    },
    [q, category, sort, feeRange, minRating, mode],
  );

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (category && category !== "all") next.set("category", category);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category]);

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main
        data-testid="patient-doctors-page"
        className="flex-1 px-6 lg:px-10 py-8 lg:py-10 max-w-7xl w-full mx-auto space-y-6"
      >
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Find a doctor</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {loading ? "Searching…" : `${total} verified specialist${total === 1 ? "" : "s"} available`}
          </p>
        </div>

        {/* Search + sort + filter */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="doctors-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search doctor or specialty…"
              className="h-12 pl-12 rounded-xl bg-white"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger
              data-testid="doctors-category-select"
              className="h-12 rounded-xl bg-white md:w-48"
            >
              <SelectValue placeholder="All specialties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All specialties</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.name}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger
              data-testid="doctors-sort-select"
              className="h-12 rounded-xl bg-white md:w-40"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rating">Top rated</SelectItem>
              <SelectItem value="fee">Lowest fee</SelectItem>
              <SelectItem value="experience">Most experienced</SelectItem>
            </SelectContent>
          </Select>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                data-testid="doctors-filter-btn"
                className="h-12 rounded-xl bg-white"
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" /> Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md">
              <SheetHeader>
                <SheetTitle>Filter doctors</SheetTitle>
              </SheetHeader>
              <div className="space-y-7 mt-6">
                <div>
                  <Label className="text-sm font-medium mb-3 inline-block">
                    Consultation fee — ₹{feeRange[0]} to ₹{feeRange[1]}
                  </Label>
                  <Slider
                    data-testid="filter-fee-slider"
                    min={0}
                    max={1500}
                    step={50}
                    value={feeRange}
                    onValueChange={setFeeRange}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-3 inline-block">
                    Minimum rating — {minRating.toFixed(1)}
                  </Label>
                  <Slider
                    data-testid="filter-rating-slider"
                    min={0}
                    max={5}
                    step={0.1}
                    value={[minRating]}
                    onValueChange={(v) => setMinRating(v[0])}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 inline-block">Mode</Label>
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger data-testid="filter-mode-select" className="h-12 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any (OPD or Video)</SelectItem>
                      <SelectItem value="video">Video only</SelectItem>
                      <SelectItem value="opd">OPD only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <SheetFooter className="mt-6 flex-col sm:flex-row gap-2">
                <Button
                  data-testid="filter-reset-btn"
                  variant="outline"
                  onClick={() => {
                    setFeeRange([0, 1500]);
                    setMinRating(0);
                    setMode("any");
                  }}
                  className="rounded-full"
                >
                  Reset
                </Button>
                <Button
                  data-testid="filter-apply-btn"
                  onClick={() => setSheetOpen(false)}
                  className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                >
                  Apply
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-56 bg-muted rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="doctors-empty-state"
            className="bg-white border border-border rounded-2xl p-10 text-center"
          >
            <Stethoscope className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <div className="font-semibold text-lg mb-1">No doctors found</div>
            <div className="text-sm text-muted-foreground">
              Try a different specialty or relax your filters.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((d) => (
              <DoctorCard key={d.id} d={d} onClick={() => navigate(`/patient/doctors/${d.id}`)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
